import React, { useState } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Badge,
  Alert,
  AlertIcon,
  Spinner,
} from "@chakra-ui/react";
import { FiCheck } from "react-icons/fi";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import { RootState } from "@src/store";
import { createMaterialReservation } from "@features/material/action";
import { MaterialType, CreateMaterialReservationParams } from "@features/material/types";
import { useTranslation } from "react-i18next";

interface MaterialOfferStepProps {
  clientID: number;
  linkedReservationID: number;
  resStart: string;
  resStop: string;
  onComplete: (reservedMaterials: MaterialType[]) => void;
  onSkip: () => void;
}

const MaterialOfferStep: React.FC<MaterialOfferStepProps> = ({
  clientID,
  linkedReservationID,
  resStart,
  resStop,
  onComplete,
  onSkip,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { availableMaterials, isLoading } = useAppSelector(
    (state: RootState) => state.material
  );

  const [wantsMaterial, setWantsMaterial] = useState<"no" | "yes">("no");
  const [selectedMaterialIds, setSelectedMaterialIds] = useState<number[]>([]);
  const [unavailableError, setUnavailableError] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const toggle = (id: number) => {
    setSelectedMaterialIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
    setUnavailableError(false);
  };

  const handleConfirm = async () => {
    if (wantsMaterial === "no") {
      onComplete([]);
      return;
    }

    if (selectedMaterialIds.length === 0) return;

    setConfirming(true);
    setUnavailableError(false);

    try {
      const reserved: MaterialType[] = [];
      for (const id of selectedMaterialIds) {
        const material = availableMaterials.find(m => m.materialTypeId === id)!;
        const params: CreateMaterialReservationParams = {
          materialTypeID: material.materialTypeId,
          clientID,
          linkedReservationID,
          quantityReserved: 1,
          resStart,
          resStop,
        };
        const result = await dispatch(createMaterialReservation(params)).unwrap();
        if (result.resultCode === 1) {
          setUnavailableError(true);
          setConfirming(false);
          return;
        }
        if (result.resultCode === 0) {
          reserved.push(material);
        }
      }
      onComplete(reserved);
    } catch {
      onComplete([]);
    } finally {
      setConfirming(false);
    }
  };

  if (isLoading) {
    return (
      <Flex justify="center" py={8}>
        <Spinner />
      </Flex>
    );
  }

  return (
    <Box>
      <Heading size="md" mb={4}>
        {t("materialOffer.title")}
      </Heading>

      <RadioGroup
        value={wantsMaterial}
        onChange={(v) => {
          setWantsMaterial(v as "no" | "yes");
          setSelectedMaterialIds([]);
          setUnavailableError(false);
        }}
        mb={4}
      >
        <Stack spacing={3}>
          <Radio value="no" size="lg" fontWeight="semibold">
            {t("materialOffer.no")}
          </Radio>
          <Radio value="yes" size="lg">
            {t("materialOffer.yes")}
          </Radio>
        </Stack>
      </RadioGroup>

      {wantsMaterial === "yes" && (
        <Box mt={4}>
          {unavailableError && (
            <Alert status="warning" mb={4} borderRadius="md">
              <AlertIcon />
              {t("materialOffer.unavailableError")}
            </Alert>
          )}

          <Stack spacing={3}>
            {availableMaterials.map((material) => {
              const isSelected = selectedMaterialIds.includes(material.materialTypeId);
              return (
                <Box
                  key={material.materialTypeId}
                  borderWidth={isSelected ? 2 : 1}
                  borderRadius="md"
                  p={4}
                  cursor="pointer"
                  borderColor={isSelected ? "custom.brand_primary" : "custom.gray_300"}
                  bg={isSelected ? "custom.gray_100" : "white"}
                  onClick={() => toggle(material.materialTypeId)}
                >
                  <Flex justify="space-between" align="flex-start">
                    <Box>
                      <Flex align="center" gap={2}>
                        <Text fontWeight="semibold">{material.name}</Text>
                        {isSelected && (
                          <Icon as={FiCheck} color="custom.brand_primary" boxSize={4} />
                        )}
                      </Flex>
                      <Text fontSize="sm" color="gray.600">
                        {material.storageLocation}
                      </Text>
                      {material.description && (
                        <Text fontSize="sm" color="gray.500" mt={1}>
                          {material.description}
                        </Text>
                      )}
                    </Box>
                    <Box textAlign="right">
                      <Badge colorScheme="green" mb={1}>
                        {material.availableQuantity} {t("materialOffer.available")}
                      </Badge>
                      <Text fontWeight="bold" fontSize="sm">
                        {material.isPaid && material.pricePerUnit != null
                          ? `€ ${material.pricePerUnit.toFixed(2)}`
                          : t("materialOffer.free")}
                      </Text>
                    </Box>
                  </Flex>
                </Box>
              );
            })}
          </Stack>
        </Box>
      )}

      <Flex mt={6} gap={3} justify="flex-end">
        <Button variant="ghost" onClick={onSkip}>
          {t("materialOffer.skip")}
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirm}
          isLoading={confirming}
          isDisabled={
            confirming ||
            (wantsMaterial === "yes" && selectedMaterialIds.length === 0)
          }
        >
          {t("materialOffer.confirm")}
        </Button>
      </Flex>
    </Box>
  );
};

export default MaterialOfferStep;
